import type { PromptTemplate } from "@/api/prompts";

export type PromptCategoryFilter = "all" | string;

export const CATEGORY_COLORS: Record<string, string> = {
  "job-research": "bg-sky-500/10 text-sky-400",
  "job-application": "bg-emerald-500/10 text-emerald-400",
  "job-evaluation": "bg-amber-500/10 text-amber-400",
  "job-interview": "bg-violet-500/10 text-violet-400",
  "job-followup": "bg-rose-500/10 text-rose-400",
  "company-research": "bg-cyan-500/10 text-cyan-400",
  "job-monitor": "bg-indigo-500/10 text-indigo-400",
  "job-offer": "bg-orange-500/10 text-orange-400",
  "job-reflection": "bg-fuchsia-500/10 text-fuchsia-400",
};

export function categoryLabel(category: string): string {
  return category
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function categoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? "bg-muted text-muted-foreground";
}

export function promptCategoryTabs(prompts: PromptTemplate[]): PromptCategoryFilter[] {
  const categories = new Set<string>();
  for (const p of prompts) {
    if (p.category) categories.add(p.category);
  }
  return ["all", ...Array.from(categories).sort()];
}

export function promptRequirementSummary(prompt: PromptTemplate): string {
  const parts: string[] = [];
  if (prompt.requiresCv) parts.push("CV");
  if (prompt.requiresProfile) parts.push("Profile");
  if (prompt.requiresMemory) parts.push("Memory");
  if (prompt.requiresDedup) parts.push("Dedup");
  return parts.length > 0 ? parts.join(" · ") : "No context required";
}

export interface PromptFilterOptions {
  search?: string;
  category?: PromptCategoryFilter;
}

export function filterPrompts(
  prompts: PromptTemplate[],
  { search = "", category = "all" }: PromptFilterOptions,
): PromptTemplate[] {
  const q = search.trim().toLowerCase();

  return prompts.filter((p) => {
    if (category !== "all" && p.category !== category) return false;
    if (q) {
      return (
        p.name.toLowerCase().includes(q) ||
        p.kind.toLowerCase().includes(q) ||
        (p.description?.toLowerCase().includes(q) ?? false) ||
        (p.category?.toLowerCase().includes(q) ?? false)
      );
    }
    return true;
  });
}

const PARSE_PATTERN_LABELS: Record<string, string> = {
  verdict: "Verdict line",
  "action-envelope": "Action envelope",
  "offer-envelope": "Offer envelope",
  "cv-envelope": "CV envelope",
  json: "JSON",
};

export function parsePatternLabel(pattern: string): string {
  return PARSE_PATTERN_LABELS[pattern] ?? pattern;
}

export function formatPromptDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function promptContextRequirements(prompt: PromptTemplate): string[] {
  const flags: string[] = [];
  if (prompt.requiresCv) flags.push("CV");
  if (prompt.requiresProfile) flags.push("Profile");
  if (prompt.requiresMemory) flags.push("Memory");
  if (prompt.requiresDedup) flags.push("Dedup");
  return flags;
}
